// US Federal Holidays, commonly observed holidays, and heritage/awareness months.
// All date calculations are local-time to avoid TZ off-by-one errors.

// Returns the date of the Nth weekday of a given month (e.g., 3rd Monday of January).
// weekday: 0=Sun, 1=Mon, ..., 6=Sat
const nthWeekdayOfMonth = (year, month, weekday, n) => {
  const firstOfMonth = new Date(year, month, 1);
  const offset = (weekday - firstOfMonth.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (n - 1) * 7);
};

// Last weekday of a given month (e.g., last Monday of May)
const lastWeekdayOfMonth = (year, month, weekday) => {
  const lastOfMonth = new Date(year, month + 1, 0); // day 0 of next month = last day
  const offset = (lastOfMonth.getDay() - weekday + 7) % 7;
  return new Date(year, month + 1, 0 - offset);
};

// Federal holidays observed when they fall on weekend (Saturday → Friday, Sunday → Monday)
const observedDate = (date) => {
  if (date.getDay() === 6) return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
  if (date.getDay() === 0) return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return date;
};

// Compute Easter Sunday using the Gauss/Meeus algorithm (Western/Gregorian)
const easterDate = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const L = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * L) / 451);
  const month = Math.floor((h + L - 7 * m + 114) / 31) - 1;
  const day = ((h + L - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
};

// Build the full holiday list for any given year
export const getHolidaysForYear = (year) => {
  const easter = easterDate(year);
  const goodFriday = new Date(year, easter.getMonth(), easter.getDate() - 2);
  const mothersDay = nthWeekdayOfMonth(year, 4, 0, 2); // 2nd Sunday of May
  const fathersDay = nthWeekdayOfMonth(year, 5, 0, 3); // 3rd Sunday of June

  return [
    // ===== Federal holidays =====
    { date: new Date(year, 0, 1), name: "New Year's Day", icon: '🎉', type: 'federal' },
    { date: nthWeekdayOfMonth(year, 0, 1, 3), name: 'Martin Luther King Jr. Day', icon: '🕊️', type: 'federal' },
    { date: nthWeekdayOfMonth(year, 1, 1, 3), name: "Presidents' Day", icon: '🇺🇸', type: 'federal' },
    { date: lastWeekdayOfMonth(year, 4, 1), name: 'Memorial Day', icon: '🇺🇸', type: 'federal' },
    { date: new Date(year, 5, 19), name: 'Juneteenth', icon: '✊🏿', type: 'federal' },
    { date: new Date(year, 6, 4), name: 'Independence Day', icon: '🇺🇸', type: 'federal' },
    { date: nthWeekdayOfMonth(year, 8, 1, 1), name: 'Labor Day', icon: '👷', type: 'federal' },
    { date: nthWeekdayOfMonth(year, 9, 1, 2), name: 'Columbus Day / Indigenous Peoples\' Day', icon: '🌎', type: 'federal' },
    { date: new Date(year, 10, 11), name: 'Veterans Day', icon: '🎖️', type: 'federal' },
    { date: nthWeekdayOfMonth(year, 10, 4, 4), name: 'Thanksgiving Day', icon: '🦃', type: 'federal' },
    { date: new Date(year, 11, 25), name: 'Christmas Day', icon: '🎄', type: 'federal' },

    // ===== Commonly observed (non-federal) =====
    { date: new Date(year, 0, 1), name: "New Year's Day (Observed)", icon: '🎊', type: 'observed', hidden: true },
    { date: new Date(year, 1, 2), name: 'Groundhog Day', icon: '🦫', type: 'observed' },
    { date: new Date(year, 1, 14), name: "Valentine's Day", icon: '💝', type: 'observed' },
    { date: new Date(year, 2, 17), name: "St. Patrick's Day", icon: '🍀', type: 'observed' },
    { date: goodFriday, name: 'Good Friday', icon: '✝️', type: 'religious' },
    { date: easter, name: 'Easter Sunday', icon: '🐣', type: 'religious' },
    { date: new Date(year, 3, 22), name: 'Earth Day', icon: '🌍', type: 'observed' },
    { date: new Date(year, 4, 5), name: 'Cinco de Mayo', icon: '🇲🇽', type: 'observed' },
    { date: mothersDay, name: "Mother's Day", icon: '💐', type: 'observed' },
    { date: fathersDay, name: "Father's Day", icon: '👔', type: 'observed' },
    { date: new Date(year, 9, 31), name: 'Halloween', icon: '🎃', type: 'observed' },
    { date: new Date(year, 10, 1), name: 'All Saints\' Day', icon: '🕯️', type: 'religious' },
    { date: new Date(year, 11, 24), name: "Christmas Eve", icon: '🌟', type: 'observed' },
    { date: new Date(year, 11, 31), name: "New Year's Eve", icon: '🎆', type: 'observed' },

    // ===== Awareness days helpful for caregivers =====
    { date: new Date(year, 8, 21), name: 'World Alzheimer\'s Day', icon: '💜', type: 'awareness' },
    { date: new Date(year, 10, 0), name: 'National Family Caregivers Day (last Friday of October — proxy: Oct 31)', icon: '🤝', type: 'awareness', hidden: true }, // skip duplicate
  ];
};

// Heritage / awareness months. Some are calendar-month aligned; Hispanic Heritage
// spans Sept 15 – Oct 15 so we mark both September and October.
export const HERITAGE_MONTHS = {
  0: [{ name: 'National Mentoring Month', icon: '🎓', color: '#0ea5e9' }],
  1: [
    { name: 'Black History Month', icon: '✊🏿', color: '#dc2626' },
    { name: 'American Heart Month', icon: '❤️', color: '#ef4444' },
  ],
  2: [
    { name: "Women's History Month", icon: '♀️', color: '#db2777' },
    { name: 'Irish-American Heritage Month', icon: '🍀', color: '#16a34a' },
  ],
  3: [
    { name: 'Arab American Heritage Month', icon: '🌙', color: '#7c3aed' },
    { name: 'Autism Acceptance Month', icon: '🧩', color: '#0ea5e9' },
    { name: 'National Volunteer Month', icon: '🤝', color: '#16a34a' },
  ],
  4: [
    { name: 'Asian American & Pacific Islander Heritage Month', icon: '🏮', color: '#dc2626' },
    { name: 'Jewish American Heritage Month', icon: '✡️', color: '#2563eb' },
    { name: 'Mental Health Awareness Month', icon: '🧠', color: '#16a34a' },
    { name: 'Older Americans Month', icon: '👴', color: '#7c3aed' },
  ],
  5: [
    { name: 'LGBTQ+ Pride Month', icon: '🏳️‍🌈', color: '#ec4899' },
    { name: 'Caribbean-American Heritage Month', icon: '🌴', color: '#0ea5e9' },
    { name: "Alzheimer's & Brain Awareness Month", icon: '💜', color: '#7c3aed' },
  ],
  6: [
    { name: 'BIPOC Mental Health Month', icon: '🧠', color: '#0ea5e9' },
    { name: 'Disability Pride Month', icon: '♿', color: '#7c3aed' },
  ],
  7: [
    { name: 'National Wellness Month', icon: '🌿', color: '#16a34a' },
  ],
  8: [
    { name: 'Hispanic Heritage Month (Sep 15 – Oct 15)', icon: '🌎', color: '#f59e0b' },
    { name: 'World Alzheimer\'s Month', icon: '💜', color: '#7c3aed' },
    { name: 'Suicide Prevention Awareness Month', icon: '🎗️', color: '#f59e0b' },
  ],
  9: [
    { name: 'Hispanic Heritage Month (continues thru Oct 15)', icon: '🌎', color: '#f59e0b' },
    { name: 'LGBTQ+ History Month', icon: '🏳️‍🌈', color: '#ec4899' },
    { name: 'Italian-American Heritage Month', icon: '🇮🇹', color: '#16a34a' },
    { name: 'Filipino-American History Month', icon: '🇵🇭', color: '#0ea5e9' },
    { name: 'Breast Cancer Awareness Month', icon: '🎗️', color: '#ec4899' },
  ],
  10: [
    { name: 'Native American Heritage Month', icon: '🪶', color: '#dc2626' },
    { name: 'National Family Caregivers Month', icon: '🤝', color: '#7c3aed' },
    { name: 'National Alzheimer\'s Disease Awareness Month', icon: '💜', color: '#7c3aed' },
  ],
  11: [
    { name: 'Universal Human Rights Month', icon: '🕊️', color: '#0ea5e9' },
    { name: 'National Hanukkah Month (varies)', icon: '🕎', color: '#2563eb' },
  ],
};

// Build a Map for O(1) holiday lookup keyed by 'YYYY-MM-DD'
export const buildHolidayMap = (year) => {
  const map = new Map();
  for (const h of getHolidaysForYear(year)) {
    if (h.hidden) continue;
    const key = `${h.date.getFullYear()}-${String(h.date.getMonth() + 1).padStart(2, '0')}-${String(h.date.getDate()).padStart(2, '0')}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(h);
  }
  return map;
};
